/**
 * "Are you sure?" — for the things where the answer matters.
 *
 * Deliberately not used for everything. A confirmation on a reversible action
 * trains people to click through confirmations, which is how the one that
 * mattered gets clicked through too. Reach for this where the act is hard to
 * undo — breaking inheritance, deleting a channel and its rules — and not for
 * moving a file to the trash, which is itself the undo.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { ErrorState } from './ErrorState';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What will happen. Say the consequence, not "this cannot be undone". */
  message: ReactNode;
  /** Defaults to the translated "Confirm". Pass one where the verb should be specific. */
  confirmLabel?: string;
  /** Defaults to the translated "Cancel". */
  cancelLabel?: string;
  /** `error` paints the confirm button red. Use it where something is lost. */
  tone?: 'primary' | 'error';
  pending?: boolean;
  /**
   * Keeps the confirm button off while Cancel stays on — the dialog is still
   * loading what it has to say, or the message explains why the act cannot go
   * ahead. Say which in `message`; a dead button with no reason is a guess.
   */
  confirmDisabled?: boolean;
  /** A failed attempt, shown inside the dialog so the context is not lost. */
  error?: Error | undefined;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'primary',
  pending = false,
  confirmDisabled = false,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}
        {error && <ErrorState error={error} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={pending}>
          {cancelLabel ?? t('action.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          color={tone}
          variant="contained"
          disabled={pending || confirmDisabled}
        >
          {confirmLabel ?? t('action.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
